<template>
  <div class="d-flex">
    <div class="mr-auto align-self-center">
      <ul class="list-unstyled mb-0">
        <li v-for="(slice, index) in slices" :key="slice.label" class="tw:flex tw:items-baseline tw:gap-2">
          <small class="mb-0 text-muted">
            <i class="fa fa-circle" :style="{ color: slice.color }"></i>
            {{ slice.label }}: {{ slice.formatted }} · {{ share(slice.value) }}%
          </small>
          <DeltaBadge v-if="slice.delta && !(hasOthers && index === slices.length - 1)" :delta="slice.delta" />
        </li>
      </ul>
      <DepartedRows :departed="metric.value.departed" />
    </div>
    <div class="align-self-center">
      <DoughnutChart :datacollection="datacollection" :options="options" :width="120" :height="120" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DoughnutChart from '@/components/charts/DoughnutChart.vue';
import DeltaBadge from './DeltaBadge.vue';
import DepartedRows from './DepartedRows.vue';
import type { DistributionMetricData, MetricDelta } from './metrics';

const props = defineProps<{
  metric: DistributionMetricData
}>();

const { t } = useI18n();

/**
 * A single-hue blue ramp anchored on the primary colour for the leading
 * slices, so no slice reads as a category break.
 */
const palette = [
  'oklch(51.83% 0.269 263.92)',
  'oklch(56% 0.244 262)',
  'oklch(60% 0.218 261)',
  'oklch(65% 0.193 259)',
  'oklch(69% 0.167 258)',
  'oklch(73% 0.142 256)',
  'oklch(77% 0.116 255)',
  'oklch(82% 0.091 253)',
  'oklch(86% 0.065 252)',
  'oklch(90% 0.040 250)',
];

/** Kept discreet so the aggregated tail never competes with the leaders. */
const neutral = 'rgb(226, 231, 237)';

const hasOthers = computed(() => props.metric.value.rows.length > palette.length);

/**
 * The circle always holds the whole: the leading slices, then every other one
 * folded into an "Others" slice. That slice gets no gap, since its membership
 * changes between two readings.
 */
const slices = computed<{ label: string, value: number, formatted: string, color: string, delta: MetricDelta | null }[]>(() => {
  const rows = props.metric.value.rows;
  const leading = rows.slice(0, palette.length).map((row, index) => ({
    label: row.label,
    value: row.value,
    formatted: row.formatted,
    color: palette[index],
    delta: row.delta,
  }));

  if (!hasOthers.value) {
    return leading;
  }

  const others = rows.slice(palette.length).reduce((total, row) => total + row.value, 0);

  return [...leading, { label: t('Others'), value: others, formatted: String(others), color: neutral, delta: null }];
});

const total = computed(() => slices.value.reduce((sum, slice) => sum + slice.value, 0));

function share(value: number): number {
  return total.value > 0 ? Math.round(value / total.value * 10000) / 100 : 0;
}

const datacollection = computed(() => ({
  labels: slices.value.map((slice) => slice.label),
  datasets: [{
    backgroundColor: slices.value.map((slice) => slice.color),
    data: slices.value.map((slice) => slice.value),
  }],
}));

const options = {
  responsive: true,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false },
  },
};
</script>
